import axios from 'axios';
import apiConfig from './apiConfig';

// Early proxy detection function
const detectCorporateProxy = () => {
  return window.location.href.includes('shield.ericomcloud.net') ||
         window.location.origin.includes('ericomcloud.net') ||
         navigator.userAgent.includes('Ericom') ||
         document.referrer.includes('ericomcloud.net');
};

// Detect proxy environment early to configure axios appropriately
const isCorporateProxy = detectCorporateProxy();

// Create axios instance with proxy-aware configuration
const axiosInstance = axios.create({
  baseURL: apiConfig.baseURL,
  headers: isCorporateProxy ? {
    // Minimal headers for corporate proxy environment to avoid CORS issues
    'Accept': 'application/json'
    // Deliberately omit X-Requested-With for proxy compatibility
  } : {
    // Standard headers for normal environment
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  },
  // Enable credentials for CORS by default (can be overridden per request)
  withCredentials: !isCorporateProxy, // Disable credentials in proxy environment
  // Configure timeouts and redirects based on environment
  timeout: isCorporateProxy ? 60000 : 30000, // Longer timeout for proxy
  maxRedirects: isCorporateProxy ? 10 : 5
});

// Log the axios instance configuration
console.log('🔧 Axios instance configured:', {
  isCorporateProxy,
  baseURL: apiConfig.baseURL,
  headers: axiosInstance.defaults.headers,
  withCredentials: axiosInstance.defaults.withCredentials,
  timeout: axiosInstance.defaults.timeout,
  maxRedirects: axiosInstance.defaults.maxRedirects
});

// Add request interceptor for debugging and FormData handling
axiosInstance.interceptors.request.use(
  config => {
    // Add authentication token if available
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Re-check proxy status in case it changed
    const currentProxyStatus = detectCorporateProxy();
    
    // Special handling for FormData uploads
    if (config.data instanceof FormData) {
      console.log('🔄 FormData upload detected - configuring headers for file upload');
      // Remove Content-Type header for FormData to let browser set it with boundary
      delete config.headers['Content-Type'];
      delete config.headers['content-type'];
      
      if (currentProxyStatus) {
        console.log('🔍 Corporate proxy detected - using absolute minimal headers for maximum compatibility');
        
        // Remove ALL potentially problematic headers for proxy environments
        const headersToRemove = [
          'Request-Id', 'request-id', 'RequestId',
          'Request-Context', 'request-context', 'RequestContext',
          'traceparent', 'Traceparent', 'TraceParent',
          'tracestate', 'Tracestate', 'TraceState',
          'x-correlation-id', 'X-Correlation-Id', 'X-CORRELATION-ID',
          'x-request-id', 'X-Request-Id', 'X-REQUEST-ID',
          'x-ms-request-id', 'X-MS-Request-Id', 'X-MS-REQUEST-ID',
          'x-ms-request-root-id', 'X-MS-Request-Root-Id', 'X-MS-REQUEST-ROOT-ID',
          'x-ms-client-request-id', 'X-MS-Client-Request-Id', 'X-MS-CLIENT-REQUEST-ID',
          'x-ms-correlation-request-id', 'X-MS-Correlation-Request-Id', 'X-MS-CORRELATION-REQUEST-ID',
          'x-ms-session-id', 'X-MS-Session-Id', 'X-MS-SESSION-ID',
          'correlation-context', 'Correlation-Context', 'CORRELATION-CONTEXT',
          'X-Requested-With', 'x-requested-with', 'X-REQUESTED-WITH',
          'Accept', 'accept', 'ACCEPT'
        ];
        
        headersToRemove.forEach(header => {
          delete config.headers[header];
        });
        
        // Disable credentials for proxy uploads
        config.withCredentials = false;
        
        console.log('🔍 Proxy environment - using absolute minimal headers for file upload');
      } else {
        // For non-proxy environments, include standard headers
        config.headers['Accept'] = 'application/json';
        if (!config.headers['X-Requested-With']) {
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
        }
      }
    } else if (!config.headers['Content-Type'] && !config.headers['content-type']) {
      // Set default Content-Type for non-FormData requests
      config.headers['Content-Type'] = 'application/json';
    }
    
    // Log the full request configuration
    console.log('🚀 Outgoing Request:', {
      url: config.url,
      method: config.method,
      baseURL: config.baseURL,
      headers: config.headers,
      withCredentials: config.withCredentials,
      dataType: config.data instanceof FormData ? 'FormData' : typeof config.data,
      hasContentType: !!(config.headers['Content-Type'] || config.headers['content-type']),
      proxyDetected: currentProxyStatus
    });
    return config;
  },
  error => {
    console.error('❌ Request Error:', {
      message: error.message,
      stack: error.stack,
      config: error.config
    });
    return Promise.reject(error);
  }
);

// Add response interceptor for debugging and token refresh
axiosInstance.interceptors.response.use(
  response => {
    console.log('✅ Response Received:', {
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
      data: response.data
    });
    return response;
  },
  async error => {
    const originalRequest = error.config;

    // Handle token refresh for 401 errors
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          const response = await axios.post(
            `${apiConfig.baseURL}/auth/refresh-token`,
            { refreshToken }
          );

          const { token: newToken, refreshToken: newRefreshToken } = response.data;
          
          localStorage.setItem('token', newToken);
          localStorage.setItem('refreshToken', newRefreshToken);
          
          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return axiosInstance(originalRequest);
        } else {
          // No refresh token, redirect to login
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          window.location.href = '/login';
        }
      } catch (refreshError) {
        // Refresh failed, redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    // Only set proxy detection flag if we see confirmed proxy signatures, not generic errors
    if (error.message && (
        error.message.includes('ericomcloud.net') ||
        error.config?.url?.includes('shield.ericomcloud.net')
      )) {
      console.warn('🔍 Proxy-related error detected (ericomcloud/shield), marking corporate proxy environment');
    }

    if (error.response) {
      console.error('❌ Response Error:', {
        status: error.response.status,
        statusText: error.response.statusText,
        headers: error.response.headers,
        data: error.response.data
      });
    } else if (error.request) {
      console.error('❌ No Response Received:', {
        request: error.request,
        message: error.message
      });
    } else {
      console.error('❌ Request Setup Error:', {
        message: error.message,
        stack: error.stack
      });
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;